import { Suspense } from "react";
import { Navigate, Route } from "react-router-dom";
import { RequirePermission } from "../permissions";
import {
  ChangePasswordRoute,
  PrivateRoute,
  PublicRoute,
  VerifyOtpRoute,
} from "./guards";
import RouteFallback from "./RouteFallback";
import {
  componentContextProps,
  layoutComponents,
  routeComponents,
} from "./routeComponents";

const GUARD_COMPONENTS = {
  public: PublicRoute,
  private: PrivateRoute,
  verifyOtp: VerifyOtpRoute,
  changePassword: ChangePasswordRoute,
};

// Component key rendered in place of any inactive route within a non-public (e.g. private)
// guard context. Keeps disabled routes defined in config (for future re-enabling) while
// surfacing the app's existing 404 experience instead of the real page.
const DISABLED_ROUTE_COMPONENT_KEY = "PageNotFound";

// Where inactive routes under a "public" guard redirect to, instead of showing a 404 -
// public/auth pages (login, register, etc.) should never expose a bare 404 to guests.
const DISABLED_PUBLIC_ROUTE_REDIRECT = "/login";

/**
 * Resolves a route config component key into a React element.
 * @param {string} componentKey
 * @param {Record<string, unknown>} routeContext
 */
function resolveRouteElement(componentKey, routeContext) {
  const Component = routeComponents[componentKey];

  if (!Component) {
    throw new Error(`[routes] Unknown component key: "${componentKey}"`);
  }

  const contextProps = componentContextProps[componentKey]?.(routeContext) ?? {};

  return (
    <Suspense fallback={<RouteFallback />}>
      <Component {...contextProps} />
    </Suspense>
  );
}

/**
 * Wraps a route element with permission gating when `permission` is declared.
 * @param {import('react').ReactElement} element
 * @param {import('./routeSchema').RouteLeaf} node
 */
function withPermissionGuard(element, node) {
  if (!node.permission) {
    return element;
  }

  return (
    <RequirePermission
      permission={node.permission}
      mode={node.permissionMode ?? "any"}
    >
      {element}
    </RequirePermission>
  );
}

/**
 * Recursively renders React Router <Route> elements from the route config tree.
 * @param {import('./routeSchema').RouteNode[]} nodes
 * @param {Record<string, unknown>} routeContext
 * @param {string} [parentKey]
 * @param {import('./routeSchema').RouteGuard | null} [ancestorGuard] - Nearest enclosing
 *   guard type (public/private), inherited by descendant nodes until overridden.
 */
export function renderRoutes(nodes, routeContext, parentKey = "root", ancestorGuard = null) {
  return nodes.map((node, index) => {
    const key = node.id ?? `${parentKey}-${index}`;

    if (node.type === "redirect") {
      return (
        <Route
          key={key}
          index={node.index}
          path={node.path}
          element={<Navigate to={node.to} replace />}
        />
      );
    }

    if (node.type === "group") {
      const guard = node.guard ?? ancestorGuard;
      const children = renderRoutes(node.children ?? [], routeContext, key, guard);
      const Guard = node.guard ? GUARD_COMPONENTS[node.guard] : null;

      if (Guard) {
        return (
          <Route key={key} element={<Guard />}>
            {children}
          </Route>
        );
      }

      return children;
    }

    if (node.type === "layout") {
      const LayoutComponent = layoutComponents[node.layout];

      if (!LayoutComponent) {
        throw new Error(`[routes] Unknown layout key: "${node.layout}"`);
      }

      return (
        <Route key={key} path={node.path} element={<LayoutComponent />}>
          {renderRoutes(node.children ?? [], routeContext, key, ancestorGuard)}
        </Route>
      );
    }

    if (node.type === "route") {
      // `active` defaults to true; setting it to false disables the route without
      // removing it from the config. Public (guest-facing) routes redirect to login
      // instead of showing a 404, since guests shouldn't see the app's 404 page.
      const isActive = node.active !== false;

      let element;
      if (!isActive && ancestorGuard === "public") {
        element = <Navigate to={DISABLED_PUBLIC_ROUTE_REDIRECT} replace />;
      } else {
        const componentKey = isActive ? node.component : DISABLED_ROUTE_COMPONENT_KEY;
        element = withPermissionGuard(
          resolveRouteElement(componentKey, routeContext),
          node
        );
      }

      return (
        <Route key={key} index={node.index} path={node.path} element={element} />
      );
    }

    return null;
  });
}

/**
 * Flattens leaf routes from the config tree for metadata lookups (nav, breadcrumbs).
 * @param {import('./routeSchema').RouteNode[]} nodes
 * @returns {Array<{ id?: string, name?: string, path: string, component: string, permission?: unknown }>}
 */
export function flattenRouteMetadata(nodes) {

  const routes = [];

  const walk = (items) => {
    items.forEach((node) => {
      if (node.type === "route" && node.path) {
        routes.push({
          id: node.id,
          name: node.name,
          path: node.path,
          component: node.component,
          permission: node.permission,
        });
      }

      if (node.children) {
        walk(node.children);
      }
    });
  };

  walk(nodes);
  return routes;
}
